import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { portalSign, portalUploadPhoto, portalDeletePhoto, extractApiError } from "@/lib/api";
import { ContractSections, SummaryTable } from "@/components/booking/ContractBody";
import { SignaturePad } from "@/components/booking/SignaturePad";

const Field = ({ label, children }) => (
  <label className="flex flex-col">
    <span className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-rust)" }}>{label}</span>
    {children}
  </label>
);

const PhotoUploader = ({ token, photos, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) await portalUploadPhoto(token, f);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(extractApiError(err, "Upload failed")); }
    setBusy(false);
    onChange();
  };
  const remove = async (id) => {
    try { await portalDeletePhoto(token, id); onChange(); } catch (err) { toast.error(extractApiError(err)); }
  };
  return (
    <div data-testid="setup-photos">
      <div className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-rust)" }}>Upload photos of the setup location (optional)</div>
      <label className="btn-outline inline-flex items-center gap-2 cursor-pointer">
        <Upload size={14} /> {busy ? "Uploading…" : "Add photos"}
        <input type="file" accept="image/*" multiple className="sr-only" onChange={upload} disabled={busy} data-testid="setup-photo-input" />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        {photos.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-2 border px-3 py-1 text-xs" style={{ borderColor: "var(--ss-border)" }} data-testid={`setup-photo-${p.id}`}>
            {p.filename}
            <button type="button" onClick={() => remove(p.id)} aria-label="Remove photo"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const Toggle = ({ value, options, onChange, prefix }) => (
  <div className="flex gap-3 pt-1">
    {options.map((v) => (
      <button type="button" key={v} data-testid={`${prefix}-${v}`} onClick={() => onChange(v)}
              className="font-label text-[0.65rem] px-4 py-2 border"
              style={{ borderColor: value === v ? "var(--ss-rust)" : "var(--ss-border)", background: value === v ? "var(--ss-rust)" : "transparent", color: value === v ? "#F5F1E8" : "var(--ss-cream)" }}>
        {v}
      </button>
    ))}
  </div>
);

const EventFields = ({ f, set, setF }) => (
  <>
    <Field label="Last Four Digits of Driver's License *"><input required inputMode="numeric" maxLength={4} pattern="\d{4}" className="editorial-input" data-testid="sign-dl4" value={f.dl_last4} onChange={set("dl_last4")} /></Field>
    <Field label="Venue Name (if applicable)"><input className="editorial-input" data-testid="sign-venue" value={f.venue_name} onChange={set("venue_name")} /></Field>
    <Field label="Event Street Address *"><input required className="editorial-input" data-testid="sign-street" value={f.event_street} onChange={set("event_street")} /></Field>
    <Field label="City *"><input required className="editorial-input" data-testid="sign-city" value={f.event_city} onChange={set("event_city")} /></Field>
    <Field label="State *">
      <select className="editorial-input" data-testid="sign-state" value={f.event_state} onChange={set("event_state")}>
        <option value="IL">Illinois</option><option value="MO">Missouri</option><option value="OTHER">Other</option>
      </select>
    </Field>
    <Field label="ZIP *"><input required inputMode="numeric" maxLength={5} pattern="\d{5}" className="editorial-input" data-testid="sign-zip" value={f.event_zip} onChange={set("event_zip")} /></Field>
    <Field label="Estimated Guest Count *"><input required type="number" min="1" className="editorial-input" data-testid="sign-guests" value={f.guest_count} onChange={set("guest_count")} /></Field>
    <Field label="Indoor or Outdoor Event? *">
      <Toggle value={f.indoor_outdoor} options={["indoor", "outdoor"]} prefix="sign" onChange={(v) => setF((p) => ({ ...p, indoor_outdoor: v }))} />
    </Field>
  </>
);

export const SigningForm = ({ token, data, onDone }) => {
  const b = data.booking;
  const cp = data.contract_preview;
  const hasService = data.quote.has_service;
  const [f, setF] = useState({
    signer_name: b.name || "", home_address: "", dl_last4: "", venue_name: "",
    event_street: "", event_city: "", event_state: hasService ? "IL" : "", event_zip: "",
    guest_count: hasService ? b.guest_count || "" : 0, indoor_outdoor: "", photo_release: "",
    signature_mode: "draw", signature_data: "", agree: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const state = hasService ? (f.event_state === "MO" ? "MO" : "IL") : cp.fixed_state === "MO" ? "MO" : "IL";

  const sections = useMemo(() => {
    const base = [...(cp.sections_by_state[state] || [])];
    if (f.photo_release) {
      const idx = base.findIndex((s) => s.key === "governing_law");
      base.splice(idx < 0 ? base.length : idx, 0, { key: "photo_release", title: "PHOTO & VIDEO RELEASE", body: cp.photo_release_options[f.photo_release] });
    }
    return base;
  }, [cp, state, f.photo_release]);

  const submit = async (e) => {
    e.preventDefault();
    if ((hasService && !f.indoor_outdoor) || !f.photo_release) return toast.error("Please complete every required field");
    if (f.signature_mode === "draw" && !f.signature_data) return toast.error("Please draw your signature");
    setBusy(true);
    try {
      await portalSign(token, { ...f, guest_count: Number(f.guest_count) || 0, template_version: cp.template_version });
      toast.success("Agreement signed");
      onDone();
    } catch (err) { toast.error(extractApiError(err, "Could not sign")); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-12" data-testid="signing-form">
      <div className="grid md:grid-cols-2 gap-8">
        <Field label="Full Name *"><input required className="editorial-input" data-testid="sign-name" value={f.signer_name} onChange={set("signer_name")} /></Field>
        <Field label="Home Address *"><input required className="editorial-input" data-testid="sign-home-address" value={f.home_address} onChange={set("home_address")} /></Field>
        {hasService && <EventFields f={f} set={set} setF={setF} />}
      </div>

      {data.needs_photos && <PhotoUploader token={token} photos={data.photos} onChange={onDone} />}

      <div>
        <h2 className="font-display text-3xl md:text-4xl mb-6">{cp.title}</h2>
        <SummaryTable rows={cp.quote_rows} />
        <div className="mt-10 p-5 md:p-8 border max-h-[70vh] overflow-y-auto" style={{ borderColor: "var(--ss-border)" }} data-testid="contract-scroll">
          <ContractSections sections={sections.filter((s) => s.key !== "photo_release")} />
        </div>
      </div>

      <div>
        <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-rust)" }}>Photo & Video Release *</div>
        <div className="space-y-2">
          {Object.entries(cp.photo_release_options).map(([k, label]) => (
            <label key={k} className="flex gap-3 items-start text-sm cursor-pointer" data-testid={`sign-release-${k}`}>
              <input type="radio" name="release" checked={f.photo_release === k} onChange={() => setF({ ...f, photo_release: k })} className="mt-1" />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-rust)" }}>Electronic Signature *</div>
        <div className="inline-flex border mb-4" style={{ borderColor: "var(--ss-rust)" }}>
          {["draw", "type"].map((m) => (
            <button type="button" key={m} data-testid={`sign-mode-${m}`} onClick={() => setF({ ...f, signature_mode: m, signature_data: "" })}
                    className="font-label text-xs px-5 py-2"
                    style={{ background: f.signature_mode === m ? "var(--ss-rust)" : "transparent", color: f.signature_mode === m ? "#F5F1E8" : "var(--ss-cream)" }}>
              {m}
            </button>
          ))}
        </div>
        {f.signature_mode === "draw"
          ? <SignaturePad onChange={(d) => setF((p) => ({ ...p, signature_data: d }))} />
          : <div className="border px-5 py-6 bg-white text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "#0F0E0C", borderColor: "var(--ss-border)" }} data-testid="typed-signature">{f.signer_name || "Your name"}</div>}
        <div className="mt-3 text-xs" style={{ color: "var(--ss-muted)" }}>Date: {new Date().toLocaleDateString()} — recorded automatically with your IP address when you sign.</div>
      </div>

      <label className="flex gap-3 items-start text-sm cursor-pointer" data-testid="sign-agree">
        <input type="checkbox" checked={f.agree} onChange={(e) => setF({ ...f, agree: e.target.checked })} className="mt-1" required />
        <span>
          I have read and agree to the {!hasService && !data.quote.executive ? cp.title : "Simply Social Rental & Service Agreement"} above, and I understand that
          {data.quote.executive
            ? " my Executive Graduation Experience event date is not reserved until this Agreement is signed and payment is made in full."
            : hasService ? " my event date is not reserved until this agreement is signed and the booking retainer is paid." : " my order is not confirmed until this agreement is signed and the order is paid in full."}
        </span>
      </label>

      <button type="submit" disabled={busy || !f.agree} className="btn-primary" data-testid="sign-submit">
        {busy ? "Signing…" : "Sign Agreement"}
      </button>
    </form>
  );
};
